import { createFileRoute } from "@tanstack/react-router";
import { ArticlePage } from "@/components/ArticlePage";
import { articleSchemas, qaSchema } from "@/lib/seo";

const UPDATED = "2026-09-29";
const ANSWER =
  "Shopify ist die richtige Wahl, wenn Sie schnell und ohne Serverarbeit verkaufen wollen: Hosting, Sicherheit und Updates sind inklusive. WooCommerce bietet mehr technische Freiheit, kostet aber laufend Pflegeaufwand. Wer wächst oder mehrere Länder bedient, fährt mit Shopify meist günstiger, weil der Betrieb planbar bleibt.";

const SITE = "https://shopify-freelancer.net";
const PATH = "/fragen/shopify-oder-woocommerce";
const TITLE = "Shopify oder WooCommerce? Der ehrliche Vergleich";
const DESCRIPTION =
  "Shopify oder WooCommerce: Kosten, Pflegeaufwand, Flexibilität und für wen sich welches System im Alltag wirklich lohnt.";

const faqs = [
  {
    q: "Ist Shopify teurer als WooCommerce?",
    a: "Auf den ersten Blick ja, weil der Tarif monatlich anfällt. Rechnet man Hosting, Wartung, Sicherheitsupdates und Plugins bei WooCommerce dazu, liegt Shopify im Alltag oft gleichauf oder günstiger.",
  },
  {
    q: "Kann ich mit WooCommerce mehr anpassen?",
    a: "Ja, der Code ist offen. Dafür tragen Sie das Risiko: Updates, Sicherheit und Server liegen bei Ihnen. Shopify ist geschlossen, dafür stabil und ohne Serverarbeit.",
  },
  {
    q: "Lohnt sich ein Wechsel von WooCommerce zu Shopify?",
    a: "Wenn Pflege und Serverprobleme Zeit fressen oder der Shop wachsen soll, ja. Produkte, Kunden und Bestellungen lassen sich sauber übernehmen.",
  },
  {
    q: "Welches System ist besser für Google?",
    a: "Beide können gut ranken. Shopify bringt saubere technische Grundlagen von Haus aus mit, bei WooCommerce hängt das stark vom Hosting und der Pflege ab.",
  },
];

export const Route = createFileRoute("/fragen/shopify-oder-woocommerce")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: `${SITE}${PATH}` },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE}${PATH}` }],
    scripts: [
      ...articleSchemas({
        title: TITLE,
        description: DESCRIPTION,
        path: PATH,
        faqs,
        section: "Shopify Vergleich",
        updated: UPDATED,
        breadcrumb: { name: "Fragen", path: "/fragen" },
      }),
      qaSchema({
        question: "Shopify oder WooCommerce?",
        answer: ANSWER,
        path: PATH,
        updated: UPDATED,
      }),
    ],
  }),
});

function Page() {
  return (
    <ArticlePage
      kicker="Frage · Vergleich"
      title="Shopify oder WooCommerce?"
      intro="Kurz gesagt: Shopify für planbaren Betrieb ohne Serverarbeit, WooCommerce für maximale technische Freiheit mit eigenem Pflegeaufwand."
      source="frage-shopify-woocommerce"
      answer={ANSWER}
      updated={UPDATED}
      breadcrumb={{ to: "/fragen", label: "Fragen" }}
      sections={[
        {
          h: "Der Unterschied in einem Satz",
          p: [
            "Shopify ist ein Komplettpaket: Hosting, Sicherheit und Updates sind dabei. WooCommerce ist ein WordPress-Plugin: Sie kümmern sich selbst um Server, Updates und Absicherung.",
          ],
        },
        {
          h: "Wann Shopify passt",
          p: [],
          list: [
            "Sie wollen verkaufen, nicht Server pflegen",
            "Der Shop soll wachsen, ohne dass die Technik zum Engpass wird",
            "Mehrere Länder, Sprachen oder Währungen sind geplant",
            "Planbare monatliche Kosten sind Ihnen wichtiger als volle Codefreiheit",
          ],
        },
        {
          h: "Wann WooCommerce passt",
          p: [],
          list: [
            "Sie haben technisches Wissen oder jemanden, der den Server betreut",
            "Sehr spezielle Anforderungen, die nur mit offenem Code gehen",
            "Der Shop bleibt bewusst klein und einfach",
          ],
        },
        {
          h: "Was ein Wechsel kostet",
          p: [
            "Eine Migration von WooCommerce zu Shopify liegt je nach Datenmenge und Funktionen meist zwischen 4.000 und 9.000 € netto. Produkte, Kunden und Bestellhistorie werden übernommen, Weiterleitungen sichern Ihre Google-Platzierungen.",
          ],
        },
      ]}
      faqs={faqs}
      related={[
        { to: "/blog/woocommerce-zu-shopify-migration", label: "WooCommerce zu Shopify wechseln" },
        { to: "/fragen/was-kostet-ein-shopify-shop", label: "Was kostet ein Shopify-Shop?" },
        { to: "/shopify-shop-erstellen-lassen", label: "Shop erstellen lassen" },
      ]}
    />
  );
}
